import { useQuery, useQueryClient, useMutation, type QueryClient } from '@tanstack/react-query';
import { doc, getDoc, collection, query, where, limit, getDocs } from 'firebase/firestore';
import { httpsCallable } from '../lib/firebase/authReadyCallable';
import { db, functions } from '../config/firebase';
import { UPDATE_GENERATOR_CALLABLE_TIMEOUT_MS } from '../config/generatorCallables';
import { COLLECTIONS } from '../config/collections';
import { isSoftDeleted } from '../lib/appsheet/softDelete';
import toast from 'react-hot-toast';
import type { Generator } from './useGenerators';
import { generatorListKeys } from './useGenerators';
import { useOrganization } from '../contexts/OrganizationContext';
import type { QBOLink } from '../types/projects';
import type { GeneratorGallerySummary } from '../types/fieldService/generatorPhotos';

/** Raw Firestore document data from appsheet_generators — AppSheet PascalCase field names. */
export interface GeneratorDoc {
  id: string;
  'Generator ID': string;
  'Generator Make': string;
  'Generator Model': string;
  'Generator Serial Number': string;
  'Generator Spec Number': string;
  'Generator Make-Model-SN': string;
  'Generator KW': string;
  'Voltage': string;
  'Fuel': string;
  'Amps': string;
  'Generator Type': string;
  'Status': string;
  'Branch': string;
  'Client Name': string;
  'Client Full Name': string;
  'Site Name': string;
  'Client Name Sub-Account': string;
  'Client Type': string;
  'Management Company': string;
  'Client Notes': string;
  'Generator Address': string;
  'Generator Address Street': string;
  'Generator Address City': string;
  'Generator Address State': string;
  'Equipment Address Zip': string;
  'GPS': string;
  'Access Instructions': string;
  'Engine Make': string;
  'Engine Model': string;
  'Engine Serial Number': string;
  'Engine Make-Model-SN': string;
  'ATS Make': string;
  'ATS Model': string;
  'ATS Serial Number': string;
  'ATS Make-Model-SN': string;
  'ATS2': string;
  'ATS 2 Make': string;
  'ATS 2 Model': string;
  'ATS 2 Serial Number': string;
  'ATS2 Make-Model-SN': string;
  'Alternator Make': string;
  'Alternator Model': string;
  'Alternator Serial Number': string;
  'Alternator Make-Model-SN': string;
  'Service Contract': string;
  'Last Completed Service Date': unknown;
  'Next Service To Schedule': unknown;
  'Recurring Service List': string;
  'OPG Installed': string;
  'Prevailing Wage': string;
  'Install Date': unknown;
  'Warranty Expires': unknown;
  'Generator Notes': string;
  'Inactive Notes': string;
  'Drive URL': string;
  'Contact ID': string[];
  /** Comma-separated AppSheet EnumList of Contact IDs (e.g. "CT2411-5B72 , CT2411-26B1"). */
  'Contacts'?: string;
  /** Virtual companion to 'Contacts' — additional inline-entered contact refs. */
  'Other Contacts Inline'?: string;
  /** Single Contact ID — FK to appsheet_shared_contacts. */
  'Primary Contact'?: string;
  'QBO_ID': string;
  'Verified': string;
  qboLinks?: QBOLink[];
  /** OfficeHub-only billing notes — not synced to AppSheet (source: officehub). */
  billingNotes?: string;
  /**
   * OfficeHub-only enrichment namespace. Only `gallery` is typed here (photo gallery summary,
   * written server-side by functions/src/fieldService/gallery/galleryService.ts); geo, pin and
   * the rest live beside it and must survive any client-side cache patch.
   */
  _opgUnit?: { gallery?: GeneratorGallerySummary | null; [key: string]: unknown };
  [key: string]: unknown;
}

/** `code` on the error thrown when one business Generator ID matches several live generators; the detail page shows that message as written. */
export const DUPLICATE_GENERATOR_ID = 'duplicate-generator-id';

export const generatorKeys = {
  all: ['generator'] as const,
  detail: (id: string) => [...generatorKeys.all, 'detail', id] as const,
};

/** Seed a partial GeneratorDoc from the generators list cache to avoid skeleton flash. */
function getInitialDataFromList(
  queryClient: ReturnType<typeof useQueryClient>,
  id: string,
  orgId: string | undefined
): GeneratorDoc | undefined {
  const list = queryClient.getQueryData<Generator[]>(generatorListKeys.list(orgId));
  const match = list?.find(g => g.id === id);
  if (!match) return undefined;
  return {
    id: match.id,
    'Generator ID': match.generatorId,
    'Client Name': match.clientName,
    'Site Name': match.siteName ?? '',
    'Generator Make': match.make ?? '',
    'Generator Model': match.model ?? '',
    'Generator Serial Number': match.serialNumber ?? '',
    'Generator KW': match.kw != null ? String(match.kw) : '',
    'Voltage': match.voltage ?? '',
    'Fuel': match.fuel ?? '',
    'Status': match.status ?? '',
    'Branch': match.branch ?? '',
    'Generator Address Street': match.address ?? '',
    'Generator Address City': match.city ?? '',
    'Generator Address State': match.state ?? '',
    'Equipment Address Zip': match.zip ?? '',
    'Engine Make': match.engineMake ?? '',
    'Engine Model': match.engineModel ?? '',
    'ATS Make': match.transferSwitchMake ?? '',
    'ATS Model': match.transferSwitchModel ?? '',
    'Generator Notes': match.notes ?? '',
  } as GeneratorDoc;
}

/**
 * Shared query key + queryFn for a single generator detail fetch.
 * Exported so callers (e.g. row hover prefetch on GeneratorsPage) can warm
 * the exact same cache entry `useGenerator` reads from, without duplicating
 * the fetch/redirect-resolution logic.
 */
export function generatorQueryOptions(queryClient: QueryClient, id: string) {
  return {
    queryKey: generatorKeys.detail(id),
    queryFn: async (): Promise<GeneratorDoc | null> => {
      if (!id) return null;
      // Inbound links carry two different identifiers: the Firestore doc ID
      // (`<appId>_<key>`, from list-derived links) and the business "Generator ID"
      // (e.g. "G2016-0277", from Typesense global search). Resolve either one.
      const snap = await getDoc(doc(db, COLLECTIONS.APPSHEET_GENERATORS, id));
      if (snap.exists()) {
        // Soft-deleted generators are treated as not found (active-view read).
        if (isSoftDeleted(snap.data())) return null;
        return { id: snap.id, ...snap.data() } as GeneratorDoc;
      }
      // No uniqueness rule backs "Generator ID" (AppSheet-synced), so ask for more than one: two live generators sharing an id
      // must be said out loud, not resolved to an arbitrary one (#2383).
      const byBusinessId = await getDocs(
        query(
          collection(db, COLLECTIONS.APPSHEET_GENERATORS),
          where('Generator ID', '==', id),
          limit(5) // more than one so a duplicate is seen; 5 is far above any real collision
        )
      );
      const live = byBusinessId.docs.filter((d) => !isSoftDeleted(d.data()));
      if (live.length > 1) {
        throw Object.assign(
          new Error(`Generator ID ${id} belongs to ${live.length} generators. Open the one you want from the Generators list.`),
          { code: DUPLICATE_GENERATOR_ID }
        );
      }
      const match = live[0];
      if (!match) return null;
      const resolved = { id: match.id, ...match.data() } as GeneratorDoc;
      // Seed the canonical doc-ID cache entry with the full doc we already have,
      // so the redirect effect's route-param change doesn't trigger a second
      // getDoc (and a transient render with only the partial list-cache fields).
      queryClient.setQueryData(generatorKeys.detail(resolved.id), resolved);
      return resolved;
    },
    staleTime: 1000 * 60 * 5,
  };
}

export function useGenerator(id: string | undefined) {
  const queryClient = useQueryClient();
  // Same org the list cached under — the seed is an EXACT key read.
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId;
  return useQuery({
    ...generatorQueryOptions(queryClient, id || ''),
    enabled: !!id,
    initialData: id ? () => getInitialDataFromList(queryClient, id, orgId) : undefined,
    // The list-derived partial has no contact fields. Stamping it with the
    // list query's updatedAt can suppress a refetch for the full staleTime,
    // leaving contacts (and other detail-only fields) permanently blank. If
    // the partial has no 'Contacts' value, force an immediate refetch by
    // reporting the initial data as already-stale (updatedAt 0).
    initialDataUpdatedAt: id
      ? () => {
          const partial = getInitialDataFromList(queryClient, id, orgId);
          const listUpdatedAt = queryClient.getQueryState(generatorListKeys.list(orgId))?.dataUpdatedAt;
          return partial && partial['Contacts'] ? listUpdatedAt : 0;
        }
      : undefined,
  });
}

const updateGeneratorFn = httpsCallable(functions, 'updateGenerator', {
  timeout: UPDATE_GENERATOR_CALLABLE_TIMEOUT_MS,
});

const FIELD_MAP: Record<string, string> = {
  generatorMake: 'Generator Make',
  generatorModel: 'Generator Model',
  generatorSerialNumber: 'Generator Serial Number',
  generatorKW: 'Generator KW',
  fuel: 'Fuel',
  voltage: 'Voltage',
  generatorType: 'Generator Type',
  generatorSpecNumber: 'Generator Spec Number',
  engineMake: 'Engine Make',
  engineModel: 'Engine Model',
  engineSerialNumber: 'Engine Serial Number',
  alternatorMake: 'Alternator Make',
  alternatorModel: 'Alternator Model',
  alternatorSerialNumber: 'Alternator Serial Number',
  atsMake: 'ATS Make',
  atsModel: 'ATS Model',
  atsSerialNumber: 'ATS Serial Number',
  ats2Make: 'ATS 2 Make',
  ats2Model: 'ATS 2 Model',
  ats2SerialNumber: 'ATS 2 Serial Number',
  clientType: 'Client Type',
  generatorNotes: 'Generator Notes',
  clientNotes: 'Client Notes',
  inactiveNotes: 'Inactive Notes',
  accessInstructions: 'Access Instructions',
  clientName: 'Client Name',
  siteName: 'Site Name',
  unitName: 'Client Name Sub-Account',
  managementCompany: 'Management Company',
  generatorAddressStreet: 'Generator Address Street',
  generatorAddressCity: 'Generator Address City',
  generatorAddressState: 'Generator Address State',
  equipmentAddressZip: 'Equipment Address Zip',
  driveUrl: 'Drive URL',
  // Keep in lockstep with the server FIELD_MAP (functions/src/projects/generatorApi.ts):
  // the Add/Edit Equipment form sends these camelCase keys for the SHARED
  // customer/address fields, so the optimistic-cache patch must map them too.
  clientNameSubAccount: 'Client Name Sub-Account',
  street: 'Generator Address Street',
  city: 'Generator Address City',
  state: 'Generator Address State',
  zip: 'Equipment Address Zip',
  gps: 'GPS',
};

export function useUpdateGenerator(generatorId: string | undefined) {
  const queryClient = useQueryClient();
  const queryKey = generatorKeys.detail(generatorId || '');

  return useMutation({
    mutationFn: async (updates: Record<string, unknown>) => {
      const result = await updateGeneratorFn({ generatorId, updates });
      return result.data;
    },
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<GeneratorDoc>(queryKey);

      if (previous) {
        const fieldUpdates: Record<string, unknown> = {};
        let qboLinksUpdate: QBOLink[] | undefined;
        let billingNotesUpdate: string | undefined;

        for (const [key, value] of Object.entries(updates)) {
          if (key === 'qboLinks') {
            qboLinksUpdate = value as QBOLink[];
          } else if (key === 'billingNotes') {
            billingNotesUpdate = value as string;
          } else {
            const mapped = FIELD_MAP[key];
            if (mapped) {
              fieldUpdates[mapped] = value;
            }
          }
        }

        const optimistic: GeneratorDoc = {
          ...previous,
          ...fieldUpdates,
          ...(qboLinksUpdate !== undefined ? { qboLinks: qboLinksUpdate } : {}),
          ...(billingNotesUpdate !== undefined ? { billingNotes: billingNotesUpdate } : {}),
        };
        queryClient.setQueryData(queryKey, optimistic);
      }

      return { previous };
    },
    onError: (err, _updates, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      // Same callable, same server budget as hooks/equipment/useGeneratorMutations.ts
      // (the sole generator mutation module; the diverged top-level duplicate is gone) —
      // a deadline-exceeded here means the browser gave up, not that the save failed.
      // No caller of this curried hook shows its own "still saving" message today, so
      // this trades a wrong "Failed" toast for silence rather than a right one; an
      // interim "still working" hint is the named follow-up, not a blocker for this fix.
      const code = (err as { code?: string } | null)?.code;
      if (code === 'functions/deadline-exceeded') return;
      toast.error('Failed to update generator');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['generators'] });
    },
    onSuccess: () => {
      toast.success('Generator updated');
    },
  });
}
